import React, { useCallback, useReducer, useContext, useEffect } from 'react';
import { createDeviceReducer } from './createDeviceReducer';
import {
	CreateDeviceContext,
	createDeviceInitialState,
	TCreateDeviceSelectedItem,
	TTypeOfModalWindow,
} from './createDeviceContext';
import { CreateDeviceActionTypes } from './createDeviceActions';
import {
	IItemForGetShortInfo,
	IServerError,
	IDeviceHardwareIdValidationError,
	ICreateDevice,
	IDeviceBankSettingForCreate,
	IRestaurantBankSettings,
	IGetRestaurantTlsCertificateInfo,
} from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getRestaurantTlsCertificateInfo } from 'backend/restaurants';

const CreateDeviceState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createDeviceReducer, createDeviceInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const { response, loading, refetch } = useFetchData<IGetRestaurantTlsCertificateInfo, string>(
		state.IDSelectedRestaurant ? getRestaurantTlsCertificateInfo : null,
		state.IDSelectedRestaurant || '',
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: CreateDeviceActionTypes.SET_TLS_CERTIFICATE_LEVEL,
				payload: {
					tlsCertificateLevel: response.data.tlsCertificateLevel,
					tlsCertificateName: response.data.tlsCertificateName,
					tlsCertificateFileId:
						response.data.tlsCertificateLevel === 'Device' ? state.tlsCertificateFileId : null,
					tlsCertificateFileHash:
						response.data.tlsCertificateLevel === 'Device' ? state.tlsCertificateFileHash : null,
				},
			});
		}
	}, [response]);

	useEffect(() => {
		if (state.IDSelectedRestaurant) {
			refetch();
		} else {
			dispatch({
				type: CreateDeviceActionTypes.SET_TLS_CERTIFICATE_LEVEL,
				payload: {
					tlsCertificateLevel: null,
					tlsCertificateName: null,
					tlsCertificateFileId: null,
					tlsCertificateFileHash: null,
				},
			});
		}
	}, [state.IDSelectedRestaurant]);

	const changeHardwareId = useCallback((value: string) => {
		dispatch({
			type: CreateDeviceActionTypes.CHANGE_HARDWARE_ID,
			payload: {
				hardwareId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDeviceActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: TCreateDeviceSelectedItem) => {
		dispatch({
			type: CreateDeviceActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDeviceActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback((value: TCreateDeviceSelectedItem) => {
		dispatch({
			type: CreateDeviceActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	}, []);

	const addBankSettingsBlock = useCallback((value: IDeviceBankSettingForCreate) => {
		dispatch({
			type: CreateDeviceActionTypes.ADD_BANK_SETTINGS_BLOCK,
			payload: {
				bankSettings: value,
			},
		});
	}, []);

	const deleteBankSettingsBlock = useCallback(
		(value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.filter((el) => el.id !== value);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeRestaurantBankSettings = useCallback(
		(settingsId: string, value: string | null) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							restaurantBankSettingId: value,
							m4BankSettingCredentials: {
								login: null,
								password: null,
							},
					  }
					: el,
			);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsTerminalId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, terminalId: value } : el,
			);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const setFetchedRestaurantBankSettings = useCallback((value: IRestaurantBankSettings[]) => {
		dispatch({
			type: CreateDeviceActionTypes.SET_FETCHED_RESTAURANT_BANK_SETTINGS,
			payload: {
				fetchedRestaurantBankSettings: value,
			},
		});
	}, []);

	const changeBankSettingsM4Login = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							m4BankSettingCredentials: {
								login: value,
								password: el.m4BankSettingCredentials?.password || null,
							},
					  }
					: el,
			);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsM4Password = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							m4BankSettingCredentials: {
								login: el.m4BankSettingCredentials?.login || null,
								password: value,
							},
					  }
					: el,
			);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsToucanLogin = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							toucanBankSettingCredentials: {
								login: value,
								password: el.toucanBankSettingCredentials?.password || null,
							},
					  }
					: el,
			);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsToucanPassword = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							toucanBankSettingCredentials: {
								login: el.toucanBankSettingCredentials?.login || null,
								password: value,
							},
					  }
					: el,
			);
			dispatch({
				type: CreateDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeTlsCertificateName = useCallback((value: string) => {
		dispatch({
			type: CreateDeviceActionTypes.CHANGE_TLS_CERTIFICATE_NAME,
			payload: {
				tlsCertificateName: value,
			},
		});
	}, []);

	const setTlsCertificateFile = useCallback(
		({ fileId, fileHash }: { fileId: Nullable<string>; fileHash: Nullable<string> }) => {
			dispatch({
				type: CreateDeviceActionTypes.SET_TLS_CERTIFICATE_FILE,
				payload: {
					tlsCertificateFileId: fileId,
					tlsCertificateFileHash: fileHash,
				},
			});
		},
		[],
	);

	const setDataForSubmit = useCallback((value: ICreateDevice | null) => {
		dispatch({
			type: CreateDeviceActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateDeviceActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IDeviceHardwareIdValidationError> | null) => {
		dispatch({
			type: CreateDeviceActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateDeviceActionTypes.RESET_FIELDS }), []);

	return (
		<CreateDeviceContext.Provider
			value={{
				changeHardwareId,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDRestaurant,
				addBankSettingsBlock,
				deleteBankSettingsBlock,
				changeRestaurantBankSettings,
				changeBankSettingsTerminalId,
				setFetchedRestaurantBankSettings,
				changeBankSettingsM4Login,
				changeBankSettingsM4Password,
				changeBankSettingsToucanLogin,
				changeBankSettingsToucanPassword,
				changeTlsCertificateName,
				setTlsCertificateFile,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				hardwareId: state.hardwareId,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				bankSettings: state.bankSettings,
				tlsCertificateLevel: state.tlsCertificateLevel,
				tlsCertificateName: state.tlsCertificateName,
				tlsCertificateFileId: state.tlsCertificateFileId,
				tlsCertificateFileHash: state.tlsCertificateFileHash,
				dataForSubmit: state.dataForSubmit,
				fetchedRestaurantBankSettings: state.fetchedRestaurantBankSettings,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CreateDeviceContext.Provider>
	);
};

export default CreateDeviceState;
